import { Grid2, Typography } from "@mui/material";
import DoneIcon from "@mui/icons-material/Done";
import React, { useCallback, useMemo, useState } from "react";
import Button from "@mui/material/Button";
import { useMutation, useQuery } from "@tanstack/react-query";
import IconButton from "@mui/material/IconButton";
import AddIcon from "@mui/icons-material/Add";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import cn from "classnames";
import { useTranslate } from "../../../hooks/useTranslate";
import Breadcrumbs from "../../../components/Breadcrumbs";
import { useStudents } from "../../../context/students";
import { useGetStudentContacts } from "../../../hooks/useGetStudentContacts";
import Preloader from "../../../components/Preloader";
import { useGetRelationTypes } from "../../../hooks/useGetRelationTypes";
import { useGetCountries } from "../../../hooks/useGetCountries";
import ContactInformationForm from "../../applicant/ResponsibleAdults/components/ContactInformationForm";
import BaseDialog from "../../../components/BaseDialog";
import axiosManager from "../../../services/axiosManager";
import SnackbarToast from "../../../components/SnackbarToast";
import ContactDetailsBox from "../../../components/ContactDetailsBox";
import AddContactTypeModal from "../../../components/AddContactTypeModal";
import { EMERGENCY_TYPE, isContactPrimary, SIMPLE_TYPE } from "../../applicant/ResponsibleAdults/constants";
import { hasPermission, PERMISSIONS } from "../../../constants/permissions";
import { useToast } from "../../../context/toast";
import "./MyDetails.css";
import { CONTACT_METHOD } from "../../../constants/contactMethod";
import { getPrimaryAddress, getPrimaryPhone } from "../../../utils/getPrimaryData";
import useStatuses from "../../../context/status/useStatuses";

const MyDetails = () => {
    const { t } = useTranslate();
    const toast = useToast();

    const { activeStudentId, permissions } = useStudents();
    const { contact: currentContact, refetchContact } = useStatuses();

    const canContactsManage = hasPermission(permissions, PERMISSIONS.CONTACTS_MANAGE);

    const user = useMemo(() => {
        if (!currentContact) return {};
        return {
            ...(currentContact || {}),
            displayName: `${currentContact.title ? `${currentContact.title} ` : ""}${currentContact.forename} ${currentContact.middleNames ? `${currentContact.middleNames} ` : ""}${currentContact.surname}`,
            isPrimary: isContactPrimary(currentContact.contactType),
            primaryAddress: getPrimaryAddress(currentContact.addresses),
            primaryPhone: getPrimaryPhone(currentContact.telephoneNumbers),
        };
    }, [currentContact]);

    const isPrimary = user?.contactType === "Primary";

    const { contacts, isLoading: isLoadingContacts, refetch } = useGetStudentContacts(isPrimary ? activeStudentId : null);
    const { relationshipsOptions, isLoading: isLoadingRelations } = useGetRelationTypes();
    const { countries, isLoading: isLoadingCountries } = useGetCountries();

    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [contactIdShown, setContactIdShown] = useState(null);
    const [addDialogOpen, setAddDialogOpen] = useState(false);
    const [newContactType, setNewContactType] = useState(null);
    const [showSuccessAccessMessage, setShowSuccessAccessMessage] = useState(false);

    const onAddContact = (contactType = SIMPLE_TYPE) => () => {
        setAddDialogOpen(false);
        setNewContactType(contactType);
    };

    const contact = contacts.length > 0 ? contacts.find((it) => it.contactId === user.contactId) : user;
    const otherContacts = contacts.filter((it) => it.contactId !== user?.contactId);

    const getRelation = useCallback((relationshipTypeId) => relationshipsOptions.find((it) => it.value === relationshipTypeId)?.label, [relationshipsOptions]);

    const {
        mutateAsync: updateContact, reset: resetUpdateContact,
        isPending: isUpdatingContact, isSuccess: isSuccessUpdateContact, error: errorUpdateContact,
    } = useMutation({
        mutationFn: (body) => axiosManager.put$(`/parent/api/contacts/${user.contactId}`, body),
    });

    const onUpdateContact = async (formData) => {
        try {
            const {
                isDirtyForm, country, proofOfId, ...formDataObj
            } = formData;
            const promises = [];

            if (isDirtyForm) {
                promises.push(updateContact({
                    ...formDataObj,
                    contactId: user.contactId,
                    countryOfResidence: country,
                }));
            }

            await Promise.all(promises);

            setIsEditModalOpen(false);
            if (isPrimary) refetch();
            refetchContact();
        } catch (err) {
            console.error(err);
        }
    };
    const onAfterAccessGrant = () => {
        setContactIdShown(null);
        setShowSuccessAccessMessage(true);

        setTimeout(() => {
            setShowSuccessAccessMessage(false);
        }, 10000);
    };

    const {
        mutate: createContact, reset: resetCreateContact,
        isPending: isCreatingContact, isSuccess: isSuccessCreateContact, error: errorCreateContact,
    } = useMutation({
        mutationFn: (body) => axiosManager.post$(`/parent/api/contacts?method=${CONTACT_METHOD.PARENT_HUB}`, body),
        onSuccess: () => {
            setNewContactType(null);
            refetch();
        },
    });

    const onCreateContact = (formData) => {
        try {
            const { country, ...bodyData } = formData;

            createContact({
                ...bodyData,
                countryOfResidence: country,
                contactType: newContactType || SIMPLE_TYPE,
            });
        } catch (err) {
            const errorData = err.response.data;

            if (errorData === "Account already exists") {
                toast.error(t("myDetails.accessModal.errorSameEmail"));
            } else {
                toast.error(t("common.errorMessage"));
            }
        }
    };

    return (
        <div className="page-content">
            <div className="student-head-section" />
            <Grid2 container spacing={2}>
                <Grid2 size={{ xs: 12, md: 7, lg: 8 }}>
                    <Breadcrumbs
                        breadcrumbs={[
                            { label: t("navigation.home"), href: activeStudentId ? `/home/${activeStudentId}` : "/" },
                            { label: t("myDetails.title"), current: true },
                        ]}
                    />
                    <Typography variant="h1" className="text-white">
                        {t("myDetails.title")}
                    </Typography>
                    <Typography variant="body1" className="mt-4 text-white" component="div">
                        {t("myDetails.subtitle")}
                    </Typography>
                </Grid2>
            </Grid2>
            <Preloader isLoading={isLoadingContacts || isLoadingRelations || isLoadingCountries}>
                <Grid2 container spacing={2} className="mt-20">
                    <Grid2 size={{ xs: 12, md: 6 }}>
                        <Typography variant="h2" component="h2" className="text-white">
                            {t("myDetails.myContactDetails")}
                        </Typography>
                        <div className="details-box mt-4">
                            <ContactDetailsBox
                                isPrimary={isPrimary}
                                showName
                                contact={contact || user}
                                countries={countries}
                                relationshipsOptions={relationshipsOptions}
                            />
                            <div className="flex justify-end">
                                <Button
                                    className="btn-link"
                                    onClick={() => setIsEditModalOpen(true)}
                                >
                                    {t("myDetails.editDetails")}
                                </Button>
                            </div>
                        </div>
                    </Grid2>
                    <Grid2 size={{ xs: 12, md: 6 }}>
                        <Typography variant="h2" component="h2" className="text-white">
                            {(canContactsManage && isPrimary) ? t("myDetails.myResponsibleContacts") : " "}
                        </Typography>
                        {showSuccessAccessMessage && (
                            <Typography variant="body1" component="div" className="mt-4 message-success text-white">
                                {t("myDetails.successAccessGrantMessage")}
                            </Typography>
                        )}
                        {(isPrimary ? otherContacts || [] : []).map((cont) => (
                            <div className="details-box mt-4">
                                <div className="flex align-center justify-between gap-4">
                                    <span className="details-box__title">
                                        {`${cont.title ? `${cont.title} ` : ""}${cont.forename} ${cont.surname}`}
                                        {cont?.relationshipTypeId && getRelation(cont?.relationshipTypeId) ? ` - ${getRelation(cont?.relationshipTypeId)}` : ""}
                                    </span>
                                    <IconButton
                                        aria-label="expand row"
                                        size="small"
                                        className="details-box__expand-btn"
                                        onClick={() => setContactIdShown((p) => (contactIdShown === cont.contactId ? null : cont.contactId))}
                                    >
                                        {contactIdShown === cont.contactId ? <KeyboardArrowUpIcon />
                                            : <KeyboardArrowDownIcon />}
                                    </IconButton>
                                </div>
                                {contactIdShown === cont.contactId ? (
                                    <>
                                        <div className="divider mt-2" />
                                        <ContactDetailsBox
                                            contact={cont}
                                            countries={countries}
                                            relationshipsOptions={relationshipsOptions}
                                            refetch={refetch}
                                            onAfterAccessGrant={onAfterAccessGrant}
                                        />
                                    </>
                                ) : null}
                            </div>
                        ))}
                        {(canContactsManage && isPrimary) && (
                            <button
                                className="details-box details-box--btn mt-4"
                                disabled={isCreatingContact}
                                onClick={() => setAddDialogOpen(true)}
                            >
                                <div className="flex align-center justify-between gap-4">
                                    <span className="details-box__title">
                                        {t("myDetails.addAnotherContact")}
                                    </span>
                                    <IconButton aria-label="add new" size="small" className="details-box__expand-btn">
                                        <AddIcon />
                                    </IconButton>
                                </div>
                            </button>
                        )}
                        <Typography variant="body1" component="div" className={cn("mt-4", !canContactsManage || !isPrimary ? "contact-help-box text-white" : "")}>
                            {t("myDetails.contactHelpText")}
                            <a href={`mailto:${t("myDetails.contactSupportEmail")}`}>{t("myDetails.contactSupportEmail")}</a>
                        </Typography>
                    </Grid2>
                </Grid2>
            </Preloader>
            <BaseDialog
                open={isEditModalOpen}
                isFullSize
                onClose={() => setIsEditModalOpen(false)}
                title={t("myDetails.updateMyDetails")}
                className="p-4"
            >
                <ContactInformationForm
                    data={contact}
                    relationshipsOptions={relationshipsOptions}
                    isLoadingRelations={isLoadingRelations}
                    isPrimaryContact
                    isShortForm
                    onCancel={() => setIsEditModalOpen(false)}
                    saveButtonText={t("myDetails.updateDetails")}
                    isSaving={isUpdatingContact}
                    onSave={onUpdateContact}
                />
            </BaseDialog>
            <AddContactTypeModal
                addDialogOpen={addDialogOpen}
                setAddDialogOpen={setAddDialogOpen}
                onAddContact={onAddContact}
            />
            <BaseDialog
                open={!!newContactType}
                isFullSize
                onClose={() => setNewContactType(null)}
                title={t("myDetails.addNewContact")}
                className="p-4"
            >
                <ContactInformationForm
                    relationshipsOptions={relationshipsOptions}
                    isLoadingRelations={isLoadingRelations}
                    isSaving={isCreatingContact}
                    onSave={onCreateContact}
                    hideTitle
                    showHubAccess={newContactType !== EMERGENCY_TYPE}
                    onCancel={() => setNewContactType(null)}
                />
            </BaseDialog>
            <SnackbarToast
                open={isSuccessUpdateContact || !!errorUpdateContact}
                isSuccess={isSuccessUpdateContact}
                isError={errorUpdateContact}
                onClose={resetUpdateContact}
            >
                {isSuccessUpdateContact
                    ? t("responsibleAdults.contactInformation.successUpdateMessage")
                    : `${t("responsibleAdults.contactInformation.errorUpdateMessage")} (status ${errorUpdateContact?.response?.status})`}
            </SnackbarToast>
            <SnackbarToast
                open={isSuccessCreateContact || !!errorCreateContact}
                isSuccess={isSuccessCreateContact}
                isError={errorCreateContact}
                onClose={resetCreateContact}
            >
                {isSuccessCreateContact
                    ? t("responsibleAdults.contactInformation.successCreateMessage")
                    : errorCreateContact?.response?.status === 400 && errorCreateContact?.response?.data === "Primary contact duplicate detected" ? t("myDetails.errorPrimaryContactDuplicate") : t("myDetails.errorCreateMessage")}
            </SnackbarToast>
        </div>
    );
};

export default MyDetails;
